import { formatDurationBetween } from '~/utils/date';

import type {
  GenerationRunDetail,
  GenerationRunRead,
  RunOrigin,
  RunProgressRead,
  RunStatus,
} from '../types/content.types';

import { failureReasonInfo } from './failure-reasons';
import { GLOSSARY } from './glossary';
import { humanizeEnum } from './run-status';

/**
 * Plain-language descriptions of a run, so the UI leads with "what was this run"
 * instead of a 36-character UUID nobody can tell apart.
 */

/** Joins a list as prose: `A`, `A & B`, `A, B & C`. */
function joinAsProse(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1] ?? ''}`;
}

/** The fields a run generated, as a readable title — e.g. "Title & Description". */
export function runFieldsLabel(run: Pick<GenerationRunRead, 'fields'>): string {
  return run.fields.length > 0 ? joinAsProse(run.fields.map(humanizeEnum)) : 'Content';
}

/** Where a run was started from, in plain words (backend `RunOrigin`). */
export const RUN_ORIGIN_WORDS: Record<RunOrigin, string> = {
  CONTENT_CONSOLE: 'This app',
  PLATFORM_SERVICE: 'Another Lily app',
  SCHEDULE: 'Schedule',
  OPERATOR: 'Operator',
};

/**
 * "Started from" for one job — names the calling service when another Lily service
 * started it. Empty for a job created before origins were recorded.
 */
export function runOriginLabel(run: Pick<GenerationRunRead, 'origin' | 'callerService'>): string {
  if (!run.origin) return '';
  const words = RUN_ORIGIN_WORDS[run.origin];
  return run.origin === 'PLATFORM_SERVICE' && run.callerService
    ? `${words} (${run.callerService})`
    : words;
}

/** The steps that work out what a product is and its details ("Understand my products"). */
const UNDERSTANDING_FLOWS = new Set(['PRODUCT_TYPE_DETECTION', 'CORE_ATTRIBUTION']);

/** The steps that score content before and after ("Score my content"). */
const SCORING_FLOWS = new Set(['CONTENT_DETERMINISTIC_SCORING', 'CONTENT_LLM_SCORING']);

/** The writing step ("Write content"); a job asking only for it is a writing job. */
const WRITING_FLOWS = new Set(['CONTENT_ENRICHMENT']);

/** What a job was for, read from the AI steps it was asked to run. */
export type RunPurpose = 'write' | 'understand' | 'score' | 'custom';

/**
 * What a job was for. A job's `fields` always lists title/description (the
 * service's default), so they can't tell an understanding job from a writing
 * one — the requested `flows` can. A writing job names only the writing step; older ones
 * name none (the backend sends the list empty for a generation-only run).
 */
export function runPurpose(run: Pick<GenerationRunRead, 'flows'>): RunPurpose {
  const { flows } = run;
  if (flows.every((flow) => WRITING_FLOWS.has(flow))) return 'write';
  if (flows.every((flow) => UNDERSTANDING_FLOWS.has(flow))) return 'understand';
  if (flows.every((flow) => SCORING_FLOWS.has(flow))) return 'score';
  return 'custom';
}

/** Whether a job writes content: no flows (the default writing job), or any flow that isn't understanding or scoring. */
export function runWrites(run: Pick<GenerationRunRead, 'flows'>): boolean {
  return (
    run.flows.length === 0 ||
    run.flows.some((flow) => !UNDERSTANDING_FLOWS.has(flow) && !SCORING_FLOWS.has(flow))
  );
}

/** Whether a job scores content — any scoring flow among its steps. */
export function runScores(run: Pick<GenerationRunRead, 'flows'>): boolean {
  return run.flows.some((flow) => SCORING_FLOWS.has(flow));
}

/** Whether a job works out what products are — any understanding flow among its steps. */
export function runUnderstands(run: Pick<GenerationRunRead, 'flows'>): boolean {
  return run.flows.some((flow) => UNDERSTANDING_FLOWS.has(flow));
}

/**
 * The run's name for its own page — the understanding step(s) it ran ("Product group detection",
 * "Attribute extraction", "Product group & attributes"), "Content scoring", or what it writes.
 */
export function runTitleLabel(run: Pick<GenerationRunRead, 'fields' | 'flows'>): string {
  switch (runPurpose(run)) {
    case 'understand': {
      // Same names as the Runs list (`runKindTitle`) and `flowLabel`, so a run reads one way.
      const detects = run.flows.includes('PRODUCT_TYPE_DETECTION');
      const extracts = run.flows.includes('CORE_ATTRIBUTION');
      if (detects && extracts) return GLOSSARY.typeAndAttributes.label;
      return detects ? 'Product group detection' : 'Attribute extraction';
    }
    case 'score':
      return 'Content scoring';
    case 'custom':
      return 'Custom run';
    case 'write':
      return runFieldsLabel(run);
  }
}

/**
 * Which products the run covered, in one short phrase. Named product ids win
 * over brand/category filters because they are the narrowest statement.
 */
export function runScopeLabel(run: Pick<GenerationRunRead, 'productFilter'>): string {
  const { productIds, brands, categories, limit } = run.productFilter;
  const parts: string[] = [];
  if (productIds.length > 0) {
    parts.push(
      `${productIds.length.toLocaleString()} selected product${productIds.length === 1 ? '' : 's'}`,
    );
  } else {
    if (brands.length > 0) parts.push(`Brand: ${brands.join(', ')}`);
    if (categories.length > 0) parts.push(`Category: ${categories.join(', ')}`);
    if (parts.length === 0) parts.push('Whole catalog');
  }
  if (limit != null) parts.push(`first ${limit.toLocaleString()}`);
  return parts.join(' · ');
}

/** First UUID segment — enough to tell runs apart at a glance; the full id stays copyable. */
export function shortRunId(runId: string): string {
  return runId.split('-')[0] ?? runId;
}

/** Wall-clock time a run took, or '' while it has not both started and finished. */
export function runDuration(run: Pick<GenerationRunRead, 'startedAt' | 'finishedAt'>): string {
  return run.startedAt && run.finishedAt
    ? formatDurationBetween(run.startedAt, run.finishedAt)
    : '';
}

/**
 * The stage, but only when it tells the reader something the status does not —
 * "Completed · Done" is noise, "Failed · at Finalizing" is the useful part.
 */
export function runStageHint(status: string, stage: string): string {
  if (!stage || status === 'COMPLETED' || status === 'CANCELLED') return '';
  if (stage.toUpperCase() === status.toUpperCase()) return '';
  const label = humanizeEnum(stage);
  return status === 'FAILED' || status === 'PAUSED' ? `at ${label}` : label;
}

/** List-page status filter: "Running" groups the three in-flight statuses into one choice. */
export type RunStatusFilter = 'ALL' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'PAUSED' | 'CANCELLED';

export const RUN_STATUS_FILTER_OPTIONS: { value: RunStatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'COMPLETED', label: 'Finished' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/** Maps the list filter onto the `statuses` the runs endpoint accepts. */
export function statusesForFilter(filter: RunStatusFilter): RunStatus[] | undefined {
  switch (filter) {
    case 'ALL':
      return undefined;
    case 'RUNNING':
      return ['PENDING', 'PLANNING', 'GENERATING'];
    default:
      return [filter];
  }
}

/** Percent of batches done, clamped to 0–100; 0 when the run has not planned its batches yet. */
export function batchPercent(completed: number, total: number): number {
  return total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
}

/** Plain meaning of each run status, for the status badge tooltip. */
const RUN_STATUS_MEANING: Record<string, string> = {
  PENDING: 'Queued — the run has been accepted and will start shortly.',
  PLANNING: 'Getting the products to include and splitting them into batches.',
  GENERATING: 'Writing content for the products, a batch at a time.',
  COMPLETED: 'Finished. Individual products can still have had problems — see the Problems tab.',
  FAILED: 'Stopped because of an error before all the work was done.',
  PAUSED: 'Paused by a person. Resume it to continue where it left off.',
  CANCELLED: 'Stopped by a person. Content that was already written is kept.',
};

/** One-sentence meaning of a run status, or '' for a value this build does not know. */
export function runStatusMeaning(status: string): string {
  return RUN_STATUS_MEANING[status] ?? '';
}

/** One reason things went wrong, with its plain label and how often it happened. */
export interface RunProblemReason {
  reason: string;
  label: string;
  count: number;
}

/** The single "how many things went wrong" figure shared by the banner, the tile and the Failures tab. */
export interface RunProblems {
  /** Failure records the run logged — falls back to failed items before metrics land. */
  total: number;
  reasons: RunProblemReason[];
  /** Short breakdown for a hint line, e.g. "AI model error". */
  hint: string;
}

/**
 * Unifies the two failure counters a run carries. `failuresByReason` counts
 * failure RECORDS (including a whole product that could not be processed),
 * while `failedItems` only counts fields that were not written — so a run can
 * have problems with zero failed items. Every surface reads this one number.
 */
export function runProblems(
  failedItems: number,
  failuresByReason: Record<string, number> | undefined,
): RunProblems {
  const reasons = Object.entries(failuresByReason ?? {})
    .filter(([, n]) => n > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([reason, count]) => ({ reason, label: failureReasonInfo(reason).label, count }));
  const recorded = reasons.reduce((sum, r) => sum + r.count, 0);
  const total = Math.max(recorded, failedItems);
  const hint =
    reasons.length === 0
      ? ''
      : reasons.length === 1
        ? (reasons[0]?.label ?? '')
        : reasons.map((r) => `${r.label} ${r.count.toLocaleString()}`).join(' · ');
  return { total, reasons, hint };
}

/** Engine stage workflows → the step a person would recognise. */
const STAGE_LABELS: [RegExp, string][] = [
  [/detect/i, 'product-group detection'],
  [/extract|attribut/i, 'attribute extraction'],
  [/generat|writ/i, 'writing'],
  [/validat|quality/i, 'scoring'],
  [/final/i, 'saving-results'],
  [/plan/i, 'batch-splitting'],
  [/fetch|load|source/i, 'getting-products'],
  [/publish|export|deliver/i, 'publishing'],
];

/** Known causes inside an engine error, most specific first. */
const CAUSE_LABELS: [RegExp, string][] = [
  [/rate.?limit|429|too many requests/i, 'the AI provider asked us to slow down'],
  [/time(d)?.?out|deadline/i, 'it ran out of time'],
  [/cancel/i, 'it was cancelled'],
  // Checked before "unavailable": a missing table/column is a setup problem, not an outage
  // (e.g. `PostgresUnavailableError: … relation "catalog.source_products" does not exist`).
  [
    /does not exist|no such (table|column)|undefined (table|column)|missing (table|schema)/i,
    'a database table it needs is missing — a setup problem on our side, not your data',
  ],
  [/unavailable|connection|unreachable|503|502/i, 'something it depends on could not be reached'],
  [/not.?found|404/i, 'something it needed could not be found'],
  [/permission|forbidden|unauthori[sz]ed|401|403/i, 'it was not allowed to access something'],
  [/activity task failed/i, 'a processing task inside it failed'],
];

/** A run error in plain words, plus the raw engine text for "Technical details". */
export interface HumanizedRunError {
  /** One plain sentence. */
  summary: string;
  /** The original message; '' when the summary already is the original. */
  technical: string;
}

/**
 * Maps the engine's raw failure string ("StageExecutionFailure: stage
 * ContentDetectionWorkflow '…' in namespace '…' … Activity task failed") to a
 * sentence a non-engineer can read, keeping the original for diagnostics.
 */
export function humanizeRunError(message: string): HumanizedRunError {
  const raw = message.trim();
  if (!raw) return { summary: '', technical: '' };

  const stageName = /stage\s+([A-Za-z]+?)(?:Workflow)?\b/.exec(raw)?.[1] ?? '';
  const stage = stageName
    ? (STAGE_LABELS.find(([pattern]) => pattern.test(stageName))?.[1] ??
      `“${stageName.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()}”`)
    : '';
  const cause = CAUSE_LABELS.find(([pattern]) => pattern.test(raw))?.[1] ?? '';
  const looksTechnical = /Failure|Exception|Error:|Traceback|namespace|workflow/i.test(raw);

  if (stage) {
    return {
      summary: `The ${stage} step failed${cause ? ` — ${cause}` : ''}.`,
      technical: raw,
    };
  }
  if (cause) {
    return {
      summary: `The run stopped because ${cause}.`,
      technical: raw,
    };
  }
  if (!looksTechnical && raw.length <= 160) return { summary: raw, technical: '' };
  return { summary: 'The run stopped because of an internal error.', technical: raw };
}

/** Which part of a job's journey an engine stage name belongs to. */
export type RunStepKind = 'products' | 'understanding' | 'writing' | 'scoring' | 'finishing';

/** Engine stage / workflow names → journey step. First match wins. */
const STEP_KIND_PATTERNS: [RegExp, RunStepKind][] = [
  [/detect|extract|attribut/i, 'understanding'],
  [/scor|validat|quality/i, 'scoring'],
  [/generat|writ|enrich/i, 'writing'],
  [/final|publish|export|deliver/i, 'finishing'],
  [/plan|fetch|load|source/i, 'products'],
];

/**
 * The journey step a raw engine error names ("stage ContentDetectionWorkflow …"
 * → understanding), or null when the message does not say which step broke.
 */
export function runErrorStepKind(message: string): RunStepKind | null {
  const stageName = /stage\s+([A-Za-z]+)/.exec(message)?.[1] ?? '';
  if (!stageName) return null;
  return STEP_KIND_PATTERNS.find(([pattern]) => pattern.test(stageName))?.[1] ?? null;
}

/** The counters to show: live workflow progress while the engine reports, the stored row otherwise. */
export function runCounters(detail: GenerationRunDetail): GenerationRunRead | RunProgressRead {
  return detail.live && detail.progress ? detail.progress : detail.run;
}

/** 0–100 progress — the engine's own figure while live, batches done otherwise. */
export function runPercent(detail: GenerationRunDetail): number {
  if (detail.live && detail.progress) return Math.round(detail.progress.percentComplete);
  return batchPercent(detail.run.completedBatches, detail.run.totalBatches);
}

/**
 * The job's error message — the engine's live one when it has one (newer than
 * the row's). The service sends '' for "no message", so fall back on emptiness.
 */
export function runErrorMessage(detail: GenerationRunDetail): string {
  const liveError = detail.live ? (detail.progress?.errorMessage ?? '') : '';
  return liveError.length > 0 ? liveError : detail.run.errorMessage;
}
